import { SpinnerButton } from "components/shared/SpinnerButton";
import { combine, isEmail, regex, required } from "shared/validations/validations";
import { ConfirmSignUpError } from "components/user/ConfirmSignUpError";
import { toastActions } from "domain/toast/toastActions";
import { confirmSignUp } from "domain/user/confirmSignUp";
import { ConfirmLicensingAccountSignUpError, confirmSignUpMutationResponse } from "generatedQueries/confirmSignUpMutation.graphql";
import { parse } from "query-string";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useRelayEnvironment } from "react-relay";
import { Redirect, useLocation } from "react-router";
import { Col, Form, Row } from "reactstrap";
import { PayloadError } from "relay-runtime";
import { routes } from "routes/routes";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { mapMessages } from "shared/mapMessages";
import { FormProvider, useForm } from "react-hook-form";

export const messages = mapMessages("components.user.signup", {
    codeValidationError: "Code is not valid",
    confirmButton: "Confirm",
    confirmationCodeLabel: "Code",
    emailAddressLabel: "Email",
    signInConfirmationSucceededToast: "Sign In confirmed",
});

interface IQueryString {
    email?: string;
    code?: string;
}

interface IConfirmSignUpForm {
    email: string;
    code: string;
}

export const ConfirmSignUpForm: FC = () => {
    const location = useLocation();
    const environment = useRelayEnvironment();
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const [failure, setFailure] = useState<boolean | ConfirmLicensingAccountSignUpError>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState("");

    const onConfirmCompleted = useCallback(
        (response: confirmSignUpMutationResponse, errors?: PayloadError[] | null) => {
            setIsSubmitting(false);
            if (errors?.length) {
                setFailure(true);
            } else if (response.confirmLicensingSignUp?.wasSuccessful) {
                setIsComplete(true);
                dispatch(toastActions.customSuccessToast(messages.signInConfirmationSucceededToast));
            } else {
                setFailure(response.confirmLicensingSignUp?.errors?.[0]?.errorCode ?? true);
            }
        },
        [dispatch]);

    const submitSignUp = useCallback(
        ({ code, email }: { email: string, code: string }) => {
            setFailure(false);
            setIsSubmitting(true);
            setSubmittedEmail(email);
            confirmSignUp(
                environment,
                email,
                code,
                onConfirmCompleted,
                onConfirmError);
        },
        [environment, onConfirmCompleted]);

    const queryString = parse(location.search) as IQueryString;
    const methods = useForm<IConfirmSignUpForm>({
        defaultValues: {
            email: queryString.email || "",
            code: queryString.code || "",
        },
    });

    useEffect(() => {
        if (queryString.email && queryString.code && !isSubmitting && !failure) {
            submitSignUp({ email: queryString.email, code: queryString.code });
        }
    }, [queryString, isSubmitting, failure, submitSignUp]);

    useEffect(() => {
        if (failure) {
            methods.setFocus("email", { shouldSelect: true });
        }
    }, [failure, methods])

    if (isComplete) {
        return <Redirect to={routes.user.login.create({})} />;
    }

    return (
        <Row>
            <Col>
                <FormProvider {...methods}>
                    <Form onSubmit={methods.handleSubmit(submitSignUp)} role="form" noValidate={true}>
                        <FormLabel
                            message={messages.emailAddressLabel}
                            for="email"
                            required={true}
                        >
                            <TextBox
                                id="email"
                                name="email"
                                type="email"
                                register={methods.register}
                                control={methods.control}
                                placeholder={formatMessage(messages.emailAddressLabel)}
                                options={combine<IConfirmSignUpForm, "email">(
                                    required(),
                                    isEmail()
                                )}
                                aria-required={true} />
                        </FormLabel>
                        <FormLabel
                            message={messages.confirmationCodeLabel}
                            for="code"
                            required={true}
                        >
                            <TextBox
                                id="code"
                                name="code"
                                register={methods.register}
                                control={methods.control}
                                placeholder={formatMessage(messages.confirmationCodeLabel)}
                                options={combine<IConfirmSignUpForm, "code">(
                                    required(),
                                    regex(/^\d{0,6}$/, messages.codeValidationError)
                                )}
                                aria-required={true} />
                        </FormLabel>
                        <ConfirmSignUpError error={failure} submittedEmail={submittedEmail} />
                        <SpinnerButton className="float-right" color="primary" isLoading={isSubmitting} message={messages.confirmButton} />
                    </Form>
                </FormProvider>
            </Col>
        </Row>
    );

    function onConfirmError() {
        setIsSubmitting(false);
        setFailure(true);
    }
};
